import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SimilarResource {
  id: string;
  title: string;
  description: string;
  type: string;
  slug: string;
  thumbnail_url: string | null;
  similarity: number;
}

interface SuggestedTag {
  id: string;
  name: string;
  group: string;
  count: number;
}

export function useResourceEmbedding() {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateEmbedding = async (resourceId: string, forceRegenerate = false): Promise<boolean> => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-resource-embedding', {
        body: { resourceId, forceRegenerate }
      });

      if (error) {
        console.error('Error generating embedding:', error);
        toast.error('Failed to generate embedding');
        return false;
      }

      if (data?.skipped) {
        console.log('Embedding generation skipped:', data.reason);
        return true;
      }

      console.log('Embedding generated:', data);
      return true;
    } catch (err) {
      console.error('Error in generateEmbedding:', err);
      toast.error('Failed to generate embedding');
      return false;
    } finally {
      setIsGenerating(false);
    }
  };

  const getSuggestedTags = async (resourceId: string): Promise<SuggestedTag[]> => {
    try {
      const { data, error } = await supabase.rpc('suggest_tags_from_similar', {
        p_resource_id: resourceId,
        p_limit: 10
      });

      if (error) {
        console.error('Error getting suggested tags:', error);
        return [];
      }

      return (data || []).map((item: any) => ({
        id: item.tag_id,
        name: item.tag_name,
        group: item.tag_group,
        count: item.occurrence_count
      }));
    } catch (err) {
      console.error('Error in getSuggestedTags:', err);
      return [];
    }
  };

  const getSimilarResources = async (resourceId: string): Promise<SimilarResource[]> => {
    try {
      // Call the RPC function using fetch directly since types may not be updated yet
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/find_similar_resources`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`
          },
          body: JSON.stringify({
            p_resource_id: resourceId,
            p_limit: 8,
            p_threshold: 0.5
          })
        }
      );

      if (!response.ok) {
        console.error('Error calling find_similar_resources:', response.status);
        return [];
      }

      const similarData = await response.json();
      return similarData || [];
    } catch (err) {
      console.error('Error in getSimilarResources:', err);
      return [];
    }
  };

  return {
    generateEmbedding,
    getSuggestedTags,
    getSimilarResources,
    isGenerating
  };
}
